import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { ChurchEventDetail, EventOptions } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { fromLocalInput, toLocalInput } from '../calendar/dates';
import { useMe } from '../session/SessionProvider';
import { PeoplePicker } from './shared';
import { ConflictsNotice } from './Conflicts';

function TextArea({ label, name, defaultValue, placeholder, hint }: { label: string; name: string; defaultValue?: string | null; placeholder?: string; hint?: string }) {
  return (
    <div className="field">
      <label htmlFor={`ev-${name}`}>{label}</label>
      <textarea id={`ev-${name}`} name={name} maxLength={2000} defaultValue={defaultValue ?? ''} placeholder={placeholder} />
      {hint ? <small className="field__hint">{hint}</small> : null}
    </div>
  );
}

/** Crear o editar un evento. */
export function EventFormPage() {
  const { eventId } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const data = useAsync(
    async (signal) => {
      if (!churchId) return null;
      const [options, event] = await Promise.all([
        api.get<EventOptions>(`/calendar/churches/${churchId}/church-events/options`, signal),
        eventId ? api.get<ChurchEventDetail>(`/calendar/churches/${churchId}/church-events/${eventId}`, signal) : Promise.resolve(null),
      ]);
      return { options, event };
    },
    [churchId, eventId],
  );

  return (
    <div className="page">
      <p className="small">
        <Link to={eventId ? `/app/eventos/${eventId}` : '/app/eventos'}>← {eventId ? 'Volver al evento' : 'Eventos'}</Link>
      </p>
      <h1>{eventId ? 'Editar evento' : 'Nuevo evento'}</h1>
      {!churchId ? <EmptyState title="Elige primero una iglesia en el selector de arriba." /> : null}
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' && data.data && churchId ? <EventForm churchId={churchId} options={data.data.options} event={data.data.event} /> : null}
    </div>
  );
}

function EventForm({ churchId, options, event }: { churchId: string; options: EventOptions; event: ChurchEventDetail | null }) {
  const navigate = useNavigate();
  const defaultStart = new Date(Date.now() + 7 * 86400_000);
  defaultStart.setHours(19, 0, 0, 0);
  const [scope, setScope] = useState<'general' | 'ministry'>(event?.scope ?? (options.canCreateGeneral ? 'general' : 'ministry'));
  const [ministryId, setMinistryId] = useState(event?.ministry?.id ?? options.ministries[0]?.id ?? '');
  const [invited, setInvited] = useState<string[]>(event?.invitedMinistries.map((m) => m.id) ?? []);
  const [people, setPeople] = useState<string[]>(event?.people.map((p) => p.personId) ?? []);
  const [tasks, setTasks] = useState<string[]>([]);
  const [taskDraft, setTaskDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [isPublic, setIsPublic] = useState(event?.isPublic ?? false);
  const [slot, setSlot] = useState(() => ({
    startsAt: toLocalInput(event ? new Date(event.startsAt) : defaultStart),
    endsAt: toLocalInput(event ? new Date(event.endsAt) : new Date(defaultStart.getTime() + 2 * 3600_000)),
    place: event?.placeName ?? '',
  }));
  function onFormChange(ev: FormEvent<HTMLFormElement>) {
    const f = new FormData(ev.currentTarget);
    const next = { startsAt: String(f.get('startsAt') ?? ''), endsAt: String(f.get('endsAt') ?? ''), place: String(f.get('placeName') ?? '') };
    if (next.startsAt !== slot.startsAt || next.endsAt !== slot.endsAt || next.place !== slot.place) setSlot(next);
  }

  if (!event && !options.canCreateGeneral && options.ministries.length === 0) {
    return <EmptyState title="Para crear eventos necesitas ser líder de un ministerio, diácono o integrante de un ministerio." />;
  }
  const ministry = options.ministries.find((m) => m.id === ministryId);
  const needsApproval = scope === 'general' ? options.generalNeedsApproval : (ministry?.needsApproval ?? true);

  function addTask() {
    const lines = taskDraft.split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.length) setTasks((t) => [...t, ...lines]);
    setTaskDraft('');
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const text = (k: string) => String(form.get(k) ?? '').trim() || null;
    const starts = String(form.get('startsAt') ?? '');
    const ends = String(form.get('endsAt') ?? '');
    if (!starts || !ends) return setError('Indica cuándo empieza y termina.');
    const body = {
      title: String(form.get('title') ?? '').trim(),
      purpose: text('purpose'),
      placeName: text('placeName'),
      address: text('address'),
      mapsUrl: text('mapsUrl'),
      startsAt: fromLocalInput(starts),
      endsAt: fromLocalInput(ends),
      isPublic,
      invitedMinistryIds: invited,
      peopleIds: people,
    };
    setBusy(true);
    setError(null);
    try {
      if (event) {
        await api.put(`/calendar/churches/${churchId}/church-events/${event.id}`, body);
        navigate(`/app/eventos/${event.id}`);
      } else {
        const pending = taskDraft.split('\n').map((l) => l.trim()).filter(Boolean);
        const created = await api.post<{ id: string }>(`/calendar/churches/${churchId}/church-events`, {
          ...body,
          scope,
          ministryId: scope === 'ministry' ? ministryId : null,
          tasks: [...tasks, ...pending].map((title) => ({ title, assigneeIds: [] })),
        });
        navigate(`/app/eventos/${created.id}`);
      }
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  const invitable = options.allMinistries.filter((m) => !(scope === 'ministry' && m.id === ministryId));

  return (
    <form className="form event-form" onSubmit={onSubmit} onChange={onFormChange} noValidate>
      {error ? <Alert>{error}</Alert> : null}
      <Card title="¿De quién es el evento?">
        {event ? (
          <p>{event.ministry ? `Evento del ministerio ${event.ministry.name}` : 'Evento general de la iglesia'}</p>
        ) : (
          <>
            <div className="segmented" role="radiogroup" aria-label="Tipo de evento">
              {options.canCreateGeneral ? (
                <label className="check">
                  <input type="radio" name="scope" checked={scope === 'general'} onChange={() => setScope('general')} /> General de la iglesia
                </label>
              ) : null}
              {options.ministries.length > 0 ? (
                <label className="check">
                  <input type="radio" name="scope" checked={scope === 'ministry'} onChange={() => setScope('ministry')} /> De un ministerio
                </label>
              ) : null}
            </div>
            {scope === 'ministry' ? (
              <div className="field">
                <label htmlFor="ev-ministry">Ministerio</label>
                <select id="ev-ministry" value={ministryId} onChange={(e) => setMinistryId(e.target.value)}>
                  {options.ministries.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <Alert tone="info">
              {needsApproval
                ? scope === 'general'
                  ? 'Lo aprueba el pastor o un diácono. Mientras tanto solo lo ven los líderes.'
                  : 'Lo autoriza el líder del ministerio. Mientras tanto solo lo ven los líderes.'
                : 'Se publica al guardarlo.'}
            </Alert>
          </>
        )}
      </Card>

      <Card title="Datos del evento">
        <Field label="Nombre o tema" name="title" required minLength={2} maxLength={150} defaultValue={event?.title} placeholder="Retiro de jóvenes" />
        <TextArea label="Propósito" name="purpose" defaultValue={event?.purpose} placeholder="¿Para qué lo hacemos? ¿Qué esperamos lograr?" />
        <div className="form__row">
          <Field label="Empieza" name="startsAt" type="datetime-local" required defaultValue={toLocalInput(event ? new Date(event.startsAt) : defaultStart)} />
          <Field
            label="Termina"
            name="endsAt"
            type="datetime-local"
            required
            defaultValue={toLocalInput(event ? new Date(event.endsAt) : new Date(defaultStart.getTime() + 2 * 3600_000))}
          />
        </div>
        <Field label="Lugar" name="placeName" maxLength={200} defaultValue={event?.placeName ?? ''} placeholder="Templo, salón, casa de…" />
        <ConflictsNotice churchId={churchId} startsAt={slot.startsAt} endsAt={slot.endsAt} place={slot.place} exclude={event?.id} />
        <Field label="Dirección" name="address" maxLength={300} defaultValue={event?.address ?? ''} />
        <Field
          label="Ubicación en el mapa"
          name="mapsUrl"
          type="url"
          maxLength={500}
          defaultValue={event?.mapsUrl ?? ''}
          placeholder="https://maps.app.goo.gl/…"
          hint="Pega el enlace de Google Maps para que lleguen fácil."
        />
        <label className="check">
          <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} /> Pública: sale en la página de la iglesia y cualquiera la ve, aunque no tenga cuenta
        </label>
      </Card>

      <Card title="¿Quiénes participan?">
        <fieldset className="picker">
          <legend>Ministerios invitados</legend>
          <p className="muted small">Cada líder acepta la invitación de su ministerio.</p>
          <ul className="picker__list picker__list--grid">
            {invitable.map((m) => (
              <li key={m.id}>
                <label className="check" htmlFor={`inv-${m.id}`}>
                  <input
                    id={`inv-${m.id}`}
                    type="checkbox"
                    checked={invited.includes(m.id)}
                    onChange={() => setInvited((v) => (v.includes(m.id) ? v.filter((x) => x !== m.id) : [...v, m.id]))}
                  />
                  {m.name}
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
        {options.people.length > 0 ? <PeoplePicker label="Personas involucradas" idPrefix="evp" people={options.people} value={people} onChange={setPeople} /> : null}
      </Card>

      {!event ? (
        <Card title="Checklist">
          <p className="muted small">Lo que hay que preparar. Después podrás asignar cada cosa, o varias a la vez, a los miembros.</p>
          {tasks.length > 0 ? (
            <ul className="checklist">
              {tasks.map((t, i) => (
                <li key={`${t}-${i}`} className="checklist__item">
                  <span>{t}</span>
                  <Button type="button" variant="ghost" onClick={() => setTasks((all) => all.filter((_, j) => j !== i))} aria-label={`Quitar ${t}`}>
                    Quitar
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
          <div className="field">
            <label htmlFor="ev-task">Nueva tarea</label>
            <textarea
              id="ev-task"
              rows={2}
              value={taskDraft}
              placeholder={'Sillas\nSonido\nComida'}
              onChange={(e) => setTaskDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  addTask();
                }
              }}
            />
            <small className="field__hint">Enter agrega la tarea. Puedes pegar varias, una por línea.</small>
          </div>
          <Button type="button" variant="secondary" onClick={addTask}>
            Agregar a la checklist
          </Button>
        </Card>
      ) : null}

      <div className="actions">
        <Button type="submit" busy={busy}>
          {event ? 'Guardar cambios' : needsApproval ? 'Enviar para aprobación' : 'Crear evento'}
        </Button>
      </div>
    </form>
  );
}
